import { BRAND } from '@archetelos/ui';
import { useQuery } from '@tanstack/react-query';
import { CircleUser, FileText, LayoutGrid, Radio } from 'lucide-react';

import { CategorySidebar } from './CategorySidebar';
import { ContinueWatchingRow } from './ContinueWatchingRow';
import { EditorialRow } from './EditorialRow';
import { HeroPlayer } from './HeroPlayer';
import { MenuBar } from './MenuBar';
import { TopBar } from './TopBar';
import { VideoRow } from './VideoRow';

import { BottomNav, type BottomNavItem } from '@/components/layout/BottomNav';
import { VideoFooter } from '@/components/layout/VideoFooter';
import { resolveLandingRoute } from '@/lib/auth/auth-redirect';
import { useAuth } from '@/lib/auth/useAuth';
import { browseQueries } from '@/lib/queries/browse';

/**
 * The public streaming browse surface — the root (`/`) for anon and members
 * alike. Responsive (Spec 32): a two-column desktop layout that reflows to a
 * single column on mobile, with the primary nav moving to a dark bottom bar.
 * All content is driven by browseQueries / catalog stubs, so an anonymous visit
 * renders the complete shell with empty/fallback states and no auth redirect.
 */
export function BrowsePage() {
  const { data: rows = [] } = useQuery(browseQueries.categoryRows());
  const { session, profile } = useAuth();

  // §2 ordering: hero, a category row, the editorial inset, then the rest.
  const [firstRow, ...restRows] = rows;

  // Mobile bottom nav (Spec 32) — the primary nav on phones (md:hidden on
  // desktop). Live / Articles are anchor / cross-surface, so they're `external`.
  const accountHref = session
    ? resolveLandingRoute({ hasSession: true, profile })
    : '/auth/sign-in';
  const bottomNavItems: BottomNavItem[] = [
    { to: '/', label: 'Browse', icon: LayoutGrid },
    { to: '/#live-now', label: 'Live', icon: Radio, external: true },
    { to: `https://${BRAND.domain}`, label: 'Articles', icon: FileText, external: true },
    { to: accountHref, label: 'Account', icon: CircleUser },
  ];

  return (
    <div className="min-h-screen bg-video-bg pb-16 text-video-text md:pb-0">
      <TopBar />
      <MenuBar />
      <div className="flex flex-col gap-8 px-4 py-6 md:flex-row md:px-6">
        <CategorySidebar />
        <main id="main" className="min-w-0 flex-1 space-y-8">
          <HeroPlayer />
          <ContinueWatchingRow />
          {firstRow && <VideoRow row={firstRow} />}
          <EditorialRow />
          {restRows.map((row) => (
            <VideoRow key={row.category.id} row={row} />
          ))}
        </main>
      </div>
      <VideoFooter />
      <BottomNav items={bottomNavItems} homeHref="/" surface="video" />
    </div>
  );
}
